import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, ArrowUp } from "lucide-react";
import { streamPost } from "@/utils/sse";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = ["Which plan is right for me?", "Tell me about plans", "Is my data safe?"];

export const WingmanChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(localStorage.getItem("wingman_session") || crypto.randomUUID());
  const scrollRef = useRef(null);

  useEffect(() => {
    localStorage.setItem("wingman_session", sessionId.current);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const clean = (text ?? input).trim();
    if (!clean || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", text: clean }, { role: "assistant", text: "" }]);
    try {
      await streamPost(
        `${API}/wingman/chat`,
        { session_id: sessionId.current, message: clean },
        null,
        (t) =>
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + t };
            return c;
          })
      );
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", text: e.message || "Connection hiccup — try again." };
        return c;
      });
    }
    setStreaming(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] bg-white border border-edge rounded-xl shadow-[0_24px_60px_-20px_rgba(0,0,0,0.18)] overflow-hidden flex flex-col"
            data-testid="wingman-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-hairline">
              <div className="flex items-center gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-azure animate-blink" />
                <span className="text-sm font-medium tracking-tight">Wingman — Doclyx copilot</span>
              </div>
              <button onClick={() => setOpen(false)} data-testid="wingman-close-button"
                className="text-mist hover:text-ink transition-colors duration-300">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            <div ref={scrollRef} className="px-5 py-4 space-y-4 max-h-80 min-h-[200px] overflow-y-auto" data-testid="wingman-messages">
              {messages.length === 0 && (
                <div className="pt-2">
                  <p className="text-sm text-fog leading-relaxed mb-4">
                    Hi — I'm Wingman. Ask me anything about Doclyx, or let me point you to the right plan.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid={`wingman-suggestion-${s.split(" ")[0].toLowerCase()}`}
                        className="text-xs text-fog border border-edge rounded-full px-3 py-1.5 hover:border-azure hover:text-azure transition-colors duration-300">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`wingman-message-${i}`}>
                  <div className={m.role === "user"
                    ? "bg-ink text-white text-sm rounded-xl rounded-br-sm px-4 py-2.5 max-w-[85%]"
                    : "text-sm text-ink leading-relaxed max-w-[90%] whitespace-pre-wrap"}>
                    {m.text || (streaming && i === messages.length - 1 ? (
                      <span className="flex gap-1 py-1" data-testid="wingman-typing">
                        <span className="w-1.5 h-1.5 rounded-full bg-mist animate-blink" />
                        <span className="w-1.5 h-1.5 rounded-full bg-mist animate-blink" style={{ animationDelay: "0.2s" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-mist animate-blink" style={{ animationDelay: "0.4s" }} />
                      </span>
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-3 border-t border-hairline px-4 py-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Wingman…"
                data-testid="wingman-input"
                className="flex-1 bg-transparent text-sm text-ink placeholder:text-mist focus:outline-none min-w-0"
              />
              <button type="submit" disabled={streaming || !input.trim()} data-testid="wingman-send-button"
                className="w-8 h-8 rounded-full bg-azure text-white flex items-center justify-center hover:bg-azuredark disabled:opacity-30 transition-colors duration-300">
                <ArrowUp className="w-4 h-4" strokeWidth={2} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 1.4 }}
        onClick={() => setOpen(!open)}
        data-testid="wingman-open-button"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-azure text-white flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(0,71,255,0.5)] hover:bg-azuredark hover:scale-105 transition-all duration-300"
        aria-label="Open Wingman chat"
      >
        <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
      </motion.button>
    </>
  );
};
